// frontend/src/app/services/native-push.service.ts
import { Injectable, inject } from '@angular/core';
import { Auth, onAuthStateChanged } from '@angular/fire/auth';
import {
  Firestore,
  doc,
  setDoc,
  serverTimestamp,
  collection,
  getDocs,
  query,
  where,
} from '@angular/fire/firestore';

import { Router } from '@angular/router';

import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed,
} from '@capacitor/push-notifications';

@Injectable({ providedIn: 'root' })
export class NativePushService {
  private auth = inject(Auth);
  private db = inject(Firestore);
  private router = inject(Router);

  private initialized = false;
  private listenersBound = false; // ✅ evita duplicar listeners si reinit

  private lastSeenWriteMs = 0;

  constructor() {}

  /**
   * Inicializa el sistema de push nativo (Android/iOS).
   * Solo hace algo si estamos en plataforma nativa (no web/PWA).
   */
  async init() {
    console.log('[NativePush] init called');

    const platform = Capacitor.getPlatform();
    console.log('[NativePush] platform:', platform);

    // 👇 En web no queremos hacer nada aquí (el push web va por WebPushService)
    if (platform === 'web') {
      console.log('[NativePush] Web platform, skipping native push init');
      return;
    }

    if (this.initialized) {
      console.log('[NativePush] Already initialized, skipping');
      return;
    }

    try {
      // ✅ 0) Bind listeners UNA sola vez (aunque reintentes init)
      if (!this.listenersBound) {
        this.listenersBound = true;

        // ✅ Listener de lifecycle para "lastSeenAt"
        App.addListener('appStateChange', async ({ isActive }) => {
          if (!isActive) return;

          const uid = this.auth.currentUser?.uid ?? (await this.waitForUser());
          if (!uid) return;

          await this.markUserSeen(uid, 'appStateChange');
        });

        // ✅ Deep-link best-effort
        App.addListener('appUrlOpen', async (data: any) => {
          try {
            const url = String(data?.url || '');
            if (!url) return;

            const uid = this.auth.currentUser?.uid ?? (await this.waitForUser());
            if (uid) await this.markUserSeen(uid, 'appUrlOpen');

            // Si luego quieres ruteo real por URL, lo conectamos.
          } catch (e) {
            console.warn('[NativePush] appUrlOpen handler failed:', e);
          }
        });

        // 3️⃣ Listener: registro correcto (token FCM/APNs)
        PushNotifications.addListener('registration', async (token: Token) => {
          console.log('[NativePush] registration event fired');
          console.log('[NativePush] registration token:', token.value);

          const uid = await this.waitForUser();
          console.log('[NativePush] uid from waitForUser:', uid);

          if (!uid) {
            console.warn('[NativePush] No user logged in, cannot save token');
            return;
          }

          const currentPlatform = Capacitor.getPlatform(); // 'android' | 'ios' | 'web'
          console.log('[NativePush] saving token for platform:', currentPlatform);

          try {
            // ✅ desactiva tokens nativos antiguos del mismo platform (best-effort)
            await this.disableOldNativeTokens(uid, currentPlatform, token.value);

            await setDoc(
              doc(this.db, `users/${uid}/pushTokens/${token.value}`),
              {
                token: token.value,
                platform: currentPlatform,
                type: 'native',
                enabled: true,
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            );

            console.log('[NativePush] token saved in Firestore');

            // ✅ marca "seen" al registrar
            await this.markUserSeen(uid, 'registration');
          } catch (err) {
            console.error('[NativePush] error saving token in Firestore', err);
          }
        });

        // 4️⃣ Listener: error en el registro
        PushNotifications.addListener('registrationError', (error) => {
          console.error('[NativePush] registration error:', error);
        });

        // 5️⃣ Listener: notificación recibida en foreground
        PushNotifications.addListener(
          'pushNotificationReceived',
          (notification: PushNotificationSchema) => {
            console.log('[NativePush] notification received:', notification);
          }
        );

        // 6️⃣ Listener: cuando el usuario toca la notificación
        PushNotifications.addListener(
          'pushNotificationActionPerformed',
          async (action: ActionPerformed) => {
            console.log('[NativePush] notification action performed:', action);

            const uid = this.auth.currentUser?.uid ?? (await this.waitForUser());
            if (uid) await this.markUserSeen(uid, 'pushAction');

            const data: any = action?.notification?.data ?? {};
            const route = typeof data.route === 'string' ? data.route : '/today/retos';

            const queryParams: any = {};
            if (data.step != null) queryParams.step = data.step;
            if (data.challengeId) queryParams.challengeId = data.challengeId;
            if (data.type) queryParams.type = data.type;

            try {
              if (Object.keys(queryParams).length) {
                await this.router.navigate([route], { queryParams });
              } else {
                await this.router.navigate([route]);
              }
            } catch (e) {
              console.warn('[NativePush] navigation failed:', e);
            }
          }
        );
      }

      // 1️⃣ Pedir permisos al sistema (después de enganchar listeners)
      const permStatus = await PushNotifications.requestPermissions();
      console.log('[NativePush] permission status:', permStatus);

      if (permStatus.receive !== 'granted') {
        console.warn('[NativePush] permission not granted');
        // ✅ IMPORTANTE: NO marcamos initialized=true si no hay permiso
        return;
      }

      // 2️⃣ Registrar el dispositivo para recibir push
      await PushNotifications.register();
      console.log('[NativePush] register called');

      // ✅ Solo aquí marcamos como inicializado (porque ya hicimos el flujo real)
      this.initialized = true;
    } catch (error) {
      console.error('[NativePush] init error:', error);
      // ✅ si falla, permitimos reintentar
      this.initialized = false;
    }
  }

  /**
   * Escribe lastSeenAt con throttle (para no spamear Firestore).
   */
  private async markUserSeen(uid: string, reason: string) {
    const now = Date.now();
    if (now - this.lastSeenWriteMs < 60_000) return;
    this.lastSeenWriteMs = now;

    try {
      await setDoc(
        doc(this.db, `users/${uid}`),
        {
          lastSeenAt: serverTimestamp(),
          lastSeenReason: reason,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      console.log('[NativePush] lastSeenAt saved:', reason);
    } catch (e) {
      console.warn('[NativePush] lastSeenAt save failed:', e);
    }
  }

  /**
   * ✅ Desactiva tokens nativos anteriores del mismo platform para ese usuario,
   * dejando activo el token actual.
   */
  private async disableOldNativeTokens(uid: string, platform: string, currentToken: string) {
    try {
      const colRef = collection(this.db, `users/${uid}/pushTokens`);
      const q = query(colRef, where('type', '==', 'native'), where('platform', '==', platform));
      const snap = await getDocs(q);

      if (snap.empty) return;

      await Promise.all(
        snap.docs
          .filter((d) => d.id !== currentToken)
          .map((d) =>
            setDoc(
              doc(this.db, d.ref.path),
              {
                enabled: false,
                disabledAt: serverTimestamp(),
                updatedAt: serverTimestamp(),
              },
              { merge: true }
            )
          )
      );
    } catch (e) {
      console.warn('[NativePush] disableOldNativeTokens failed (best-effort):', e);
    }
  }

  /**
   * Espera a que haya un usuario autenticado y devuelve su uid.
   */
  private waitForUser(): Promise<string | null> {
    const u = this.auth.currentUser;
    if (u?.uid) {
      console.log('[NativePush] waitForUser: currentUser already set:', u.uid);
      return Promise.resolve(u.uid);
    }

    console.log('[NativePush] waitForUser: waiting for auth state...');
    return new Promise((resolve) => {
      const off = onAuthStateChanged(this.auth, (usr) => {
        off();
        console.log('[NativePush] onAuthStateChanged fired, uid:', usr?.uid);
        resolve(usr?.uid ?? null);
      });
    });
  }
}
